<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  slaInfo: {
    type: Object,
    required: true,
  },
  channelInfo: {
    type: Object,
    required: true,
  },
  sourceInfo: {
    type: Object,
    required: true,
  },
  teamName: {
    type: String,
    default: null,
  },
  dealValue: {
    type: Number,
    default: 0,
  },
  dealStatus: {
    type: String,
    default: 'open', // 'open' | 'won' | 'lost'
  },
  formattedCurrency: {
    type: String,
    default: 'R$ 0,00',
  },
  formattedDateTime: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'dragstart',
  'assign-me',
  'update-deal-value',
  'delete-card',
  'edit-card',
]);

const { t } = useI18n();
const router = useRouter();

const isEditingValue = ref(false);
const editValueInput = ref(props.dealValue || 0);

const contact = computed(() => props.conversation.meta?.sender || {});
const assignee = computed(() => props.conversation.meta?.assignee || null);
const isUnread = computed(() => (props.conversation.unread_count || 0) > 0);

const contactAvatarUrl = computed(() => {
  return (
    contact.value.thumbnail ||
    contact.value.avatar_url ||
    contact.value.avatar ||
    ''
  );
});

const assigneeAvatarUrl = computed(() => {
  return (
    assignee.value?.thumbnail ||
    assignee.value?.avatar_url ||
    assignee.value?.avatar ||
    ''
  );
});

const companyName = computed(() => {
  return (
    contact.value.additional_attributes?.company_name ||
    contact.value.custom_attributes?.company_name ||
    null
  );
});

// Filter out stage labels and clean # prefix
const cleanedLabels = computed(() => {
  const labels = props.conversation.labels || [];
  return labels.map(lbl => lbl.replace(/^#/, ''));
});

// Card State Precedence & Colors matching reference images (kanban_01.jpg -> kanban_04.jpg):
// 1. Lost (kanban_03.jpg) -> Header: #F8B4B4, Border: #F87171, Body: #FDECEC
// 2. Won (kanban_04.jpg) -> Header: #C5E86C, Border: #84CC16, Body: #F1F8E3
// 3. Novo (kanban_01.jpg) -> Header: #FCEFC7, Border: #E8B923, Body: #FFFFFF + Novo pill
// 4. Default (kanban_02.jpg) -> Header: #F3F4F6, Border: #E5E7EB, Body: #FFFFFF
const cardStateClasses = computed(() => {
  if (props.dealStatus === 'lost') {
    return {
      border: 'border-[#F87171] dark:border-rose-800',
      header: 'bg-[#F8B4B4] dark:bg-rose-950/60',
      body: 'bg-[#FDECEC] dark:bg-rose-950/20',
    };
  }
  if (props.dealStatus === 'won') {
    return {
      border: 'border-[#84CC16] dark:border-lime-700',
      header: 'bg-[#C5E86C] dark:bg-lime-950/60',
      body: 'bg-[#F1F8E3] dark:bg-lime-950/20',
    };
  }
  if (isUnread.value) {
    return {
      border: 'border-[#E8B923] dark:border-amber-700',
      header: 'bg-[#FCEFC7] dark:bg-amber-950/40',
      body: 'bg-white dark:bg-slate-900',
    };
  }
  return {
    border: 'border-[#E5E7EB] dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700',
    header: 'bg-[#F3F4F6] dark:bg-slate-800/60',
    body: 'bg-white dark:bg-slate-900',
  };
});

const slaBadgeClasses = computed(() => {
  switch (props.slaInfo.slaLevel) {
    case 'critical':
      return {
        container:
          'bg-rose-50 dark:bg-rose-950/60 border-rose-200 dark:border-rose-900/80 text-rose-600 dark:text-rose-400 font-extrabold animate-pulse',
        icon: 'text-rose-600 dark:text-rose-400',
      };
    case 'warning':
      return {
        container:
          'bg-amber-50 dark:bg-amber-950/60 border-amber-200 dark:border-amber-900/80 text-amber-600 dark:text-amber-400 font-bold',
        icon: 'text-amber-600 dark:text-amber-400',
      };
    default:
      return {
        container:
          'bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 font-medium',
        icon: 'text-slate-500 dark:text-slate-400',
      };
  }
});

function openConversation(event) {
  if (
    event.target.closest('button') ||
    event.target.closest('input') ||
    event.target.closest('select')
  ) {
    return;
  }
  const accountId = props.conversation.account_id;
  const id = props.conversation.id;
  if (accountId && id) {
    router
      .push({
        name: 'inbox_conversation',
        params: { accountId, conversation_id: id },
      })
      .catch(() => {
        window.location.href = `/app/accounts/${accountId}/conversations/${id}`;
      });
  }
}

function handleDragStart(event) {
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData('text/plain', String(props.conversation.id));
  emit('dragstart', event, props.conversation);
}

function saveValueEdit() {
  isEditingValue.value = false;
  emit('update-deal-value', props.conversation.id, editValueInput.value);
}
</script>

<template>
  <div
    draggable="true"
    class="group relative flex flex-col overflow-hidden rounded-2xl border-[1.5px] shadow-xs transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md cursor-grab active:cursor-grabbing select-none"
    :class="[cardStateClasses.border, cardStateClasses.body]"
    @dragstart="handleDragStart"
    @click="openConversation"
  >
    <!-- HEADER BAND: state badge (left) + Atribuir/SLA (right) -->
    <div
      class="flex items-center justify-between gap-2 px-3 py-2 min-h-[38px]"
      :class="cardStateClasses.header"
    >
      <!-- Left: 'Novo' Pill Badge + Team (if present) -->
      <div class="flex items-center gap-1.5 min-w-0">
        <!-- 'Novo' Pill Badge with inner lime dot (matching reference) -->
        <span
          v-if="isUnread"
          class="inline-flex items-center gap-1.5 rounded-full bg-[#84CC16] px-2.5 py-0.5 text-[11px] font-extrabold text-[#1a3a08] shadow-2xs flex-shrink-0 animate-pulse"
          title="Nova mensagem / Atividade não lida"
        >
          <span class="size-2 rounded-full bg-[#bef264] border border-[#65a30d]" />
          Novo
        </span>

        <!-- Team Badge (if present) -->
        <span
          v-if="teamName"
          class="inline-flex items-center rounded-md bg-blue-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-blue-600 border border-blue-500/20 truncate max-w-[80px]"
          :title="`Time: ${teamName}`"
        >
          {{ teamName }}
        </span>
      </div>

      <!-- Right: Atribuir Button + SLA time badge -->
      <div class="flex items-center gap-2 flex-shrink-0">
        <button
          v-if="!assignee"
          type="button"
          class="inline-flex items-center gap-1 rounded-full bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 px-2.5 py-0.5 text-[11px] font-semibold text-slate-700 dark:text-slate-200 border border-slate-300/80 dark:border-slate-700 transition-colors shadow-2xs cursor-pointer"
          title="Atribuir a mim"
          @click.stop="emit('assign-me', conversation.id)"
        >
          <Icon icon="i-lucide-user-plus" class="size-3 text-slate-600 dark:text-slate-300" />
          <span>Atribuir</span>
        </button>

        <!-- SLA Wait Time Badge com cores dinâmicas (Cinza <=30m | Laranja 30-60m | Vermelho >1h piscando) -->
        <span
          class="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] shadow-2xs"
          :class="slaBadgeClasses.container"
          :title="t('CRM.CARD.ELAPSED_TIME')"
        >
          <Icon icon="i-lucide-clock" class="size-3" :class="slaBadgeClasses.icon" />
          <span>{{ slaInfo.formattedTime }}</span>
        </span>
      </div>
    </div>

    <!-- BODY -->
    <div class="flex flex-col gap-2.5 p-3.5" :class="cardStateClasses.body">
      <!-- TOP LEAD ROW: Lead Avatar (left) + Name & Phone -->
      <div class="flex items-center gap-3 min-w-0">
        <!-- Lead Avatar with WhatsApp overlay icon -->
        <div class="relative flex-shrink-0">
          <Avatar
            :name="contact.name || 'Contato'"
            :src="contactAvatarUrl"
            size="42"
            rounded-full
            class="rounded-full border-2 border-white dark:border-slate-800 shadow-xs object-cover"
          />

          <!-- WhatsApp Overlay Badge (bottom-right of avatar) -->
          <div
            class="absolute -bottom-0.5 -right-0.5 flex size-[19px] items-center justify-center rounded-full bg-[#25D366] text-white border-2 border-white dark:border-slate-900 shadow-xs"
            :title="`Canal: ${channelInfo.name}`"
          >
            <Icon icon="i-lucide-message-circle" class="size-2.5 text-white" />
          </div>
        </div>

        <!-- Lead Name & Phone -->
        <div class="flex flex-col min-w-0 flex-1">
          <span
            class="truncate text-sm font-extrabold text-slate-900 dark:text-slate-100 leading-tight hover:text-woot-500 transition-colors"
            :title="contact.name || 'Contato Sem Nome'"
          >
            {{ contact.name || 'Contato Sem Nome' }}
          </span>

          <span
            v-if="companyName"
            class="truncate text-xs font-semibold text-woot-500 mt-0.5"
          >
            {{ companyName }}
          </span>
          <span
            v-else
            class="truncate text-xs font-semibold text-slate-700 dark:text-slate-300 mt-0.5"
          >
            {{ contact.phone_number || contact.email || `#${conversation.id}` }}
          </span>
        </div>
      </div>

      <!-- MIDDLE ROW: Channel Pill & Source Pill + Assignee Avatar on the right -->
      <div class="flex flex-col gap-1.5 w-full">
        <!-- 1. Channel Pill: Gray background with green dot and text "WhatsApp" -->
        <div class="flex items-center">
          <div
            class="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-bold bg-[#E5E7EB] dark:bg-slate-800 text-slate-800 dark:text-slate-200 border border-slate-300/70 dark:border-slate-700 shadow-2xs"
          >
            <span
              class="size-2 rounded-full"
              :style="{ backgroundColor: channelInfo.brandHex || '#25D366' }"
            />
            <span>{{ channelInfo.name }}</span>
          </div>
        </div>

        <!-- 2. Source Row: Source Pill (Left) & Assignee Avatar (Right) on the same line -->
        <div class="flex items-center justify-between gap-2 w-full">
          <!-- Source Pill: White pill with official platform logo and "Origem: Google Ads" -->
          <div
            class="inline-flex items-center gap-2 rounded-md pl-1.5 pr-2.5 py-1 text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xs text-slate-700 dark:text-slate-200"
            :title="`${t('CRM.CARD.SOURCE_LABEL')} ${sourceInfo.name}`"
          >
            <span
              class="flex items-center justify-center size-5 flex-shrink-0 rounded bg-white dark:bg-slate-700 border border-slate-200/80 dark:border-slate-600 shadow-2xs p-0.5"
            >
              <Icon :icon="sourceInfo.icon" class="size-4" />
            </span>
            <span class="truncate max-w-[190px]">
              Origem: <strong>{{ sourceInfo.name }}</strong>
            </span>
          </div>

          <!-- Assignee Avatar (Agent) aligned right on the same line as Source pill (without online green dot) -->
          <div
            v-if="assignee"
            class="flex-shrink-0"
            :title="`Atendente: ${assignee.name}`"
          >
            <Avatar
              :name="assignee.name || 'Atendente'"
              :src="assigneeAvatarUrl"
              size="34"
              rounded-full
              class="rounded-full border-2 border-white dark:border-slate-800 shadow-2xs object-cover"
            />
          </div>
        </div>
      </div>

      <!-- LEAD ID (Left) & DEAL VALUE (Right) -->
      <div class="flex items-center justify-between gap-2 mt-0.5">
        <span class="text-xs text-slate-800 dark:text-slate-300 font-medium">
          Lead ID: <strong class="text-slate-900 dark:text-slate-100 font-extrabold">{{ conversation.id }}</strong>
        </span>

        <div class="flex items-center flex-shrink-0">
          <template v-if="isEditingValue">
            <input
              v-model="editValueInput"
              type="number"
              step="0.01"
              class="w-24 rounded-lg border border-woot-500 bg-white dark:bg-slate-900 px-2 py-1 text-xs text-right font-bold text-slate-900 dark:text-slate-100 focus:outline-none shadow-2xs"
              @keyup.enter="saveValueEdit"
              @blur="saveValueEdit"
            />
          </template>
          <template v-else>
            <button
              type="button"
              class="group/val flex items-center gap-1.5 rounded-lg px-3 py-1 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-xs font-extrabold text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 shadow-2xs transition-colors cursor-pointer"
              :title="t('CRM.CARD.EDIT_VALUE')"
              @click.stop="isEditingValue = true"
            >
              <span>{{ formattedCurrency }}</span>
              <Icon
                icon="i-lucide-edit-2"
                class="size-2.5 opacity-0 group-hover/val:opacity-100 text-slate-400 transition-opacity"
              />
            </button>
          </template>
        </div>
      </div>

      <!-- FOOTER: Date & Time (Left) + Action Buttons (Right: Trash & Eye in white pill) -->
      <div
        class="flex items-center justify-between border-t border-slate-200/80 dark:border-slate-800 pt-2.5 mt-1 text-xs text-slate-600 dark:text-slate-400 font-semibold"
      >
        <!-- Date & Time (dd/mm/aaaa | hh:mm:ss) with green calendar icon -->
        <span
          class="flex items-center gap-1.5 text-slate-700 dark:text-slate-300 select-none text-[11px] font-medium"
          title="Data e hora de entrada"
        >
          <Icon icon="i-lucide-calendar" class="size-3.5 text-[#84CC16]" />
          <span>{{ formattedDateTime || 'N/A' }}</span>
        </span>

        <!-- Action Buttons Pill (Trash + Eye) -->
        <div
          class="flex items-center gap-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-1 shadow-2xs"
        >
          <button
            type="button"
            class="p-1 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors cursor-pointer flex items-center justify-center"
            title="Remover Card do Kanban (não apaga o contato nem as mensagens)"
            @click.stop="emit('delete-card', conversation.id)"
          >
            <Icon icon="i-lucide-trash-2" class="size-3.5" />
          </button>
          <button
            type="button"
            class="p-1 rounded-md text-slate-400 hover:text-woot-500 hover:bg-woot-50 dark:hover:bg-woot-950/30 transition-colors cursor-pointer flex items-center justify-center"
            :title="t('CRM.CARD.VIEW_DETAILS')"
            @click.stop="emit('edit-card', conversation)"
          >
            <Icon icon="i-lucide-eye" class="size-3.5" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
